import {
  useRef,
  useState,
  FC,
  Children,
  LegacyRef,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  TouchEvent,
} from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { IconStyle } from '../Icons/Icons';
import { useDeviceTypeMediaQuery } from '../hooks/useDeviceTypeMediaQuery';
import { InfiniteCarouselPropTypes } from './types/propTypes';
import CarouselButton from './CarouselButton';
import styles from './Carousel.module.scss';
import CarouselDot from './CarouselDot';
import { getDotsPadding, getCarouselContainerConfig, dotsToDisplayArr, getCarouselEvent } from './utils';

const cx = classNames.bind(styles);

const autoScrollEventAction = (
  handleNext: () => void,
  noOfScrollSecs: number,
  isAutoScrollEnable: boolean,
  intervalId: string | number | undefined | NodeJS.Timeout,
  setIntervalId: (value: string | number | undefined | NodeJS.Timeout) => void,
) => {
  // Remove AutoScroll Interval
  const removeInterval = () => {
    clearInterval(intervalId);
  };
  // Resume AutoScroll Interval
  const resumeInterval = () => {
    removeInterval();
    const interval = setInterval(() => {
      handleNext();
    }, noOfScrollSecs * 1000);
    setIntervalId(isAutoScrollEnable ? interval : undefined);
  };
  return { removeInterval, resumeInterval };
};

const InfiniteCarousel: FC<InfiniteCarouselPropTypes> = forwardRef<
  HTMLDivElement | undefined,
  InfiniteCarouselPropTypes
>(
  (
    {
      children,
      noOfScrollSecs = 5,
      isAutoScrollEnable = false,
      pauseOnMouseOver = false,
      isTileCarousel = true,
      dotsPaddingTop,
      hideDots,
      hideButtons,
      className = '',
      id,
    },
    ref,
  ) => {
    const originalChildList = Children.toArray(children);
    const { isMobile, isTablet } = useDeviceTypeMediaQuery();

    const [visibleAreaMapping, setVisibleAreaMapping] = useState<Record<string, number>>();
    const [intervalId, setIntervalId] = useState<string | number | undefined | NodeJS.Timeout>(undefined);
    const [carouselDotCount, setCarouselDotCount] = useState<number>(0);
    const [activeCarouselDot, setActiveCarouselDot] = useState<number>(0);
    const [dotsPaddingTopValue, setDotsPaddingTopValue] = useState<number | string>();

    const [childNodes, setChildNodes] = useState([...originalChildList]);
    const [touchPosition, setTouchPosition] = useState<Record<string, number>>({
      touchstart: 0, //event names
      touchmove: 0,
    });

    useImperativeHandle(ref, () => carouselContainer.current);

    //  the Refs
    const containerRef = useRef<HTMLDivElement | null>(null);
    const carouselContainer = useRef<HTMLDivElement>();
    const carouselItem = useRef<HTMLDivElement[]>([]);
    const safeScrollAvailable = useRef<number>(0);
    const indexRef = useRef<number>(0);
    const initialSlideOffset = useRef<number>(0);
    const isMoving = useRef<boolean>(false);
    const wheelLock = useRef<boolean>(false);
    const deltaAcc = useRef<number>(0);
    const wheelTimeout = useRef<string | number | undefined | NodeJS.Timeout>(undefined);

    const totalRealItems = originalChildList.length;

    const { clientWidth, scrollWidth } = getCarouselContainerConfig(carouselContainer);

    const processConfig = () => {
      if (clientWidth) {
        const itemWidth = carouselItem?.current?.[0]?.clientWidth as number;
        const gutterSize = 16;

        let itemsInAFrame = clientWidth / itemWidth;
        itemsInAFrame = (clientWidth - itemsInAFrame * gutterSize) / itemWidth;
        if (itemWidth >= clientWidth) {
          itemsInAFrame = 1;
        }

        const safeItemsInAFrame = Math.floor(itemsInAFrame);
        const dotCount = Math.ceil(safeItemsInAFrame ? totalRealItems / safeItemsInAFrame : scrollWidth / clientWidth);
        setCarouselDotCount(isTileCarousel ? dotCount : originalChildList.length);

        const extendedChildNodes = [
          ...originalChildList.slice(-Math.ceil(itemsInAFrame)),
          ...originalChildList,
          ...originalChildList.slice(0, Math.ceil(itemsInAFrame) + 1),
        ];
        setChildNodes(extendedChildNodes);
        initialSlideOffset.current = isTileCarousel
          ? Math.floor(Math.ceil(itemsInAFrame) * (itemWidth + gutterSize)) || 0
          : itemWidth;

        safeScrollAvailable.current = isTileCarousel
          ? Math.min(safeItemsInAFrame * (itemWidth + gutterSize), clientWidth)
          : itemWidth;
      }
    };

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
        return;
      }
      if (wheelLock.current) {
        return;
      }

      deltaAcc.current += e.deltaX;

      const slideWidth = containerRef.current?.offsetWidth ?? clientWidth;
      const threshold = isTileCarousel
        ? slideWidth * 0.12
        : (carouselItem?.current?.[0]?.clientWidth || clientWidth) * 0.72;

      if (Math.abs(deltaAcc.current) > threshold) {
        deltaAcc.current > 0 ? handleNext() : handlePrev();
        deltaAcc.current = 0;
        wheelLock.current = true;
        if (wheelTimeout.current) {
          clearTimeout(wheelTimeout.current);
        }

        wheelTimeout.current = setTimeout(() => {
          wheelLock.current = false;
        }, 1000);
      }
    };

    const updatePosition = (transition: boolean = true) => {
      if (!containerRef.current) {
        return;
      }
      const nextSlidePX = -`${visibleAreaMapping?.[`${indexRef.current}`]}`;
      containerRef.current.style.transition = transition ? 'transform 0.5s cubic-bezier(0.2, 0, 0, 1)' : 'none';
      containerRef.current.style.transform = `translate3d(${nextSlidePX}px, 0, 0)`;
    };

    const syncDots = (value: number) => {
      if (value >= carouselDotCount) {
        setActiveCarouselDot(0);
      } else if (value < 0) {
        setActiveCarouselDot(carouselDotCount - 1);
      } else {
        setActiveCarouselDot(value);
      }
    };

    const handleNext = () => {
      if (isMoving.current) {
        return;
      }
      isMoving.current = true;
      const nextFrameIs = indexRef.current + 1;
      indexRef.current = nextFrameIs;
      updatePosition();
      syncDots(indexRef.current);
    };

    const handlePrev = () => {
      if (isMoving.current) {
        return;
      }
      isMoving.current = true;
      const prevFrameIs = indexRef.current - 1;
      indexRef.current = prevFrameIs;
      updatePosition();
      syncDots(indexRef.current);
    };

    const handleTransitionEnd = () => {
      isMoving.current = false;
      // Snapping Logic
      if (indexRef.current >= carouselDotCount) {
        indexRef.current = 0;
        updatePosition(false);
      } else if (indexRef.current < 0) {
        indexRef.current = carouselDotCount - 1;
        updatePosition(false);
      }
    };

    const goToDot = (dotIndex: number) => {
      if (isMoving.current) {
        return;
      }
      indexRef.current = dotIndex;
      updatePosition();
      syncDots(indexRef.current);
    };
    const { resumeInterval, removeInterval } = autoScrollEventAction(
      handleNext,
      noOfScrollSecs,
      isAutoScrollEnable,
      intervalId,
      setIntervalId,
    );

    useEffect(() => {
      if (safeScrollAvailable.current && carouselDotCount) {
        let currentVisibleAreaMapping: Record<string, number> = { '-1': 0 };

        const safeScroll = safeScrollAvailable.current;

        [...dotsToDisplayArr(carouselDotCount + 1)].forEach((_, index) => {
          let checkpoint = Math.min(safeScroll * index, scrollWidth);
          if (index && index + 1 === carouselDotCount && currentVisibleAreaMapping[index - 1] !== undefined) {
            const prevCheckpoint = currentVisibleAreaMapping[index - 1] as number;
            checkpoint = prevCheckpoint + (scrollWidth - (prevCheckpoint + clientWidth));
          }
          currentVisibleAreaMapping = {
            ...currentVisibleAreaMapping,
            [`${index}`]: isTileCarousel
              ? checkpoint + initialSlideOffset.current
              : initialSlideOffset.current * (index + 1),
          };
        });
        setVisibleAreaMapping(currentVisibleAreaMapping);
      }
    }, [carouselDotCount, safeScrollAvailable.current]);

    useLayoutEffect(() => {
      processConfig();
      const padding = getDotsPadding(isMobile, isTablet, dotsPaddingTop);
      setDotsPaddingTopValue(padding);
    }, [isMobile, isTablet, clientWidth, scrollWidth]);

    useEffect(() => {
      if (isAutoScrollEnable) {
        resumeInterval();
      } else {
        removeInterval();
      }
      return () => {
        removeInterval();
      };
    }, [isAutoScrollEnable, noOfScrollSecs, visibleAreaMapping]);
    const handleTouch = (e: TouchEvent<HTMLDivElement>) => {
      carouselContainer.current?.classList.remove(styles['scroll-control'] as string);
      setTouchPosition((prevState) => ({
        ...prevState,
        [e.type]: e.touches[0]?.clientX as number,
      }));
    };
    const handleTouchEnd = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { touchstart, touchmove }: any = touchPosition;
      const diffX = touchstart - touchmove;

      if (touchmove && diffX) {
        if (diffX > 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }
      setTouchPosition({ touchstart: 0, touchmove: 0 });
    };

    const CCarouselClassname = cx('carousel', {
      'infinite-carousel-container': true,
      'tile-carousel': isTileCarousel,
      'banner-carousel': !isTileCarousel,
    });

    const CCarouselMainBodyClassname = cx('carousel-main-body', {
      'tile-carousel': isTileCarousel,
      'banner-carousel': !isTileCarousel,
    });

    return (
      <div className={`${styles['container-carousel']} ${className}`}>
        <div
          ref={carouselContainer as LegacyRef<HTMLDivElement>}
          className={CCarouselClassname}
          {...getCarouselEvent({
            removeInterval,
            resumeInterval,
            ...{ ...optionalProp('isAutoScrollEnable', isAutoScrollEnable) },
            ...{ ...optionalProp('pauseOnMouseOver', pauseOnMouseOver) },
          })}
          onTouchStart={handleTouch}
          onTouchMove={handleTouch}
          onTouchEnd={handleTouchEnd}
          data-testid="carousel"
          id={id}
        >
          {!hideButtons && (
            <CarouselButton
              id="carousel-button-left"
              icon="chevron-left"
              iconStyle={IconStyle.SOLID}
              iconClass={styles['carousel-button-left'] as string}
              data-testid="carousel-button-left"
              clickHandler={handlePrev}
            />
          )}
          <div
            ref={containerRef}
            onTransitionEnd={handleTransitionEnd}
            className={CCarouselMainBodyClassname}
            style={{
              transform: `translate3d(-${visibleAreaMapping?.[indexRef.current] || 0}px, 0, 0)`,
            }}
            onWheel={handleWheel}
            data-testid="carousel-track"
          >
            {childNodes.map((e, i) => (
              <div
                className={`${styles['carousel-item']}}`}
                data-testid={`carousel-item-${i}`}
                ref={(el: HTMLDivElement) => (carouselItem.current[i] = el)}
                key={`node${+i}`}
                id={`carousel-item-${i}`}
                style={{
                  minWidth: isTileCarousel ? 'auto' : '100%',
                }}
              >
                {e}
              </div>
            ))}
          </div>
          {!hideButtons && (
            <CarouselButton
              id="carousel-button-right"
              icon="chevron-right"
              iconStyle={IconStyle.SOLID}
              data-testid="carousel-button-right"
              iconClass={styles['carousel-button-right'] as string}
              clickHandler={handleNext}
            />
          )}
        </div>
        <div>
          {!hideDots && carouselDotCount > 1 && (
            <div
              className={`${styles['carousel-ellipses']} ${!isTileCarousel ? styles['inset'] : ''}`}
              style={{ paddingTop: isTileCarousel ? dotsPaddingTopValue : 0 }}
              data-testid="carousel-dot-container"
            >
              {[...dotsToDisplayArr(carouselDotCount)].map((_, i) => (
                <CarouselDot
                  index={i}
                  key={`dot-${+i}`}
                  isActive={activeCarouselDot === i}
                  clickHandler={() => goToDot(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);
InfiniteCarousel.displayName = 'InfiniteCarousel';
export default InfiniteCarousel;
